import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Forum as ForumIcon,
  Announcement as AnnouncementIcon,
  MenuBook as GuidelinesIcon,
  AccountTree as NetworkIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { useAuth } from '../../contexts/AuthContext'

function CommunityLayout({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuth()

  const [themeMode, setThemeMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode')
    return savedMode || 'light'
  })
  const darkMode = themeMode === 'dark'

  const [anchorEl, setAnchorEl] = useState(null)
  const menuOpen = Boolean(anchorEl)

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode)
  }, [themeMode])

  const toggleTheme = () => {
    setThemeMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'))
  }

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    await signOut()
    handleMenuClose()
    window.location.href = '/'
  }

  const tabs = [
    { label: 'Discussions', path: '/community', icon: <ForumIcon /> },
    { label: 'Announcements', path: '/community/announcements', icon: <AnnouncementIcon /> },
    { label: 'Guidelines', path: '/community/guidelines', icon: <GuidelinesIcon /> },
  ]

  const currentTab = tabs.findIndex(tab => location.pathname === tab.path)

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: '#3b82f6' },
      secondary: { main: '#8b5cf6' },
      background: {
        default: darkMode ? '#0f172a' : '#ffffff',
        paper: darkMode ? '#1e293b' : '#ffffff',
      },
    },
  })

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: 'background-color 0.3s ease',
        }}
      >
        {/* Header */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            background: darkMode
              ? 'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.85) 100%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.85) 100%)',
            backdropFilter: 'blur(20px)',
            borderBottom: 1,
            borderColor: darkMode ? '#334155' : '#e2e8f0',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ px: 0 }}>
              {/* Logo */}
              <Box
                onClick={() => navigate('/')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mr: 4,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <NetworkIcon sx={{ fontSize: 20, color: 'white' }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: '1.1rem',
                  }}
                >
                  Community
                </Typography>
              </Box>

              <Box sx={{ flexGrow: 1 }} />

              {/* Theme Toggle */}
              <IconButton
                onClick={toggleTheme}
                sx={{
                  mr: 2,
                  bgcolor: darkMode ? '#1e293b' : '#f1f5f9',
                  color: darkMode ? '#fbbf24' : '#3b82f6',
                  transition: 'all 0.5s ease',
                  '&:hover': {
                    transform: 'rotate(180deg)',
                    bgcolor: darkMode ? '#334155' : '#e2e8f0',
                  },
                }}
              >
                {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>

              {/* User Menu */}
              {user && (
                <>
                  <IconButton
                    onClick={handleMenuOpen}
                    sx={{
                      bgcolor: darkMode ? '#1e293b' : '#f1f5f9',
                      border: '2px solid',
                      borderColor: '#3b82f6',
                      padding: 0,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    {user?.user_metadata?.avatar_url ? (
                      <Avatar src={user.user_metadata.avatar_url} sx={{ width: 36, height: 36 }} />
                    ) : (
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                        }}
                      >
                        {user?.user_metadata?.full_name?.charAt(0).toUpperCase() ||
                          user?.email?.charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                  </IconButton>

                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    sx={{ mt: 1 }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                      sx: {
                        bgcolor: darkMode ? '#1e293b' : '#fff',
                        border: darkMode ? '1px solid #334155' : '1px solid #e2e8f0',
                        minWidth: 220,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: darkMode ? '#fff' : '#0f172a' }}
                      >
                        {user?.user_metadata?.full_name ||
                          user?.user_metadata?.name ||
                          user?.email?.split('@')[0]}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: darkMode ? '#94a3b8' : '#64748b' }}
                      >
                        {user?.email}
                      </Typography>
                    </Box>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        handleMenuClose()
                        localStorage.setItem('profileReferrer', '/community')
                        navigate('/profile')
                      }}
                      sx={{
                        py: 1.5,
                        '&:hover': { bgcolor: darkMode ? '#334155' : '#f8fafc' },
                      }}
                    >
                      <ListItemIcon>
                        <PersonIcon fontSize="small" sx={{ color: '#8b5cf6' }} />
                      </ListItemIcon>
                      <ListItemText>Profile Settings</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMenuClose()
                        navigate('/app')
                      }}
                      sx={{
                        py: 1.5,
                        '&:hover': { bgcolor: darkMode ? '#334155' : '#f8fafc' },
                      }}
                    >
                      <ListItemIcon>
                        <DashboardIcon fontSize="small" sx={{ color: '#3b82f6' }} />
                      </ListItemIcon>
                      <ListItemText>Go to Workspace</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={handleLogout}
                      sx={{
                        py: 1.5,
                        '&:hover': { bgcolor: darkMode ? '#334155' : '#f8fafc' },
                      }}
                    >
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" sx={{ color: '#ef4444' }} />
                      </ListItemIcon>
                      <ListItemText sx={{ color: '#ef4444' }}>Logout</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Toolbar>
          </Container>
        </AppBar>

        {/* Content */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Tabs
            value={currentTab === -1 ? false : currentTab}
            onChange={(e, newValue) => navigate(tabs[newValue].path)}
            sx={{
              mb: 4,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            ))}
          </Tabs>

          {children}
        </Container>
      </Box>
    </ThemeProvider>
  )
}

export default CommunityLayout
